import { useEffect, useState } from "react";
import {
  LuRefreshCw,
  LuX,
  LuTrash2,
  LuChevronDown,
  LuChevronRight,
} from "react-icons/lu";
import Eye from "assets/unityMenu/Eye.png";
import EyeCrossed from "assets/unityMenu/EyeCrossed.png";
import classNames from "utilities/ClassNames";
import { motion, AnimatePresence } from "framer-motion";

const Measurement = ({
  data,
  type,
  setSelectedFigure,
  selectedFigure,
  setFigureToRemove,
  setPointToRemove,
  setFigureVisibility,
  setVolumeVisibility,
  setIsHidden
}: {
  data: any;
  type: "volume" | "distanceAndArea";
  setSelectedFigure: React.Dispatch<React.SetStateAction<number | 'none'>>;
  selectedFigure: number | 'none';
  setFigureToRemove: React.Dispatch<React.SetStateAction<number | null>>;
  setPointToRemove: React.Dispatch<React.SetStateAction<{ id: number | null, index: number | null }>>;
  setFigureVisibility: React.Dispatch<React.SetStateAction<{ id: number | null, isVisible: boolean | null }>>;
  setVolumeVisibility: React.Dispatch<React.SetStateAction<{ id: number | null, index: number | null, isVisible: boolean | null }>>;
  setIsHidden: (args: { type: number, payload: { id: number | null } }) => void;
}) => {
  const [volumeToReload, setVolumeToReload] = useState<null | number>(null);

  useEffect(() => {
    if (volumeToReload !== null) {
      const message = JSON.stringify({ object: "MeasurementReactUI", type: "MeasurementReactUIController", method: "RefreshVolume", arguments: [JSON.stringify(volumeToReload)] });
      window.vuplex.postMessage(message);
    }
    setVolumeToReload(null);
  }, [volumeToReload]);

  if (type === "distanceAndArea") {
    const isArea = data.points.length > 2;
    return (
      <div className={classNames("flex flex-wrap items-center gap-2 bg-dark/35 w-full px-4 py-2 rounded-lg", selectedFigure === data.id ? "border-green border-2" : "cursor-pointer")} onClick={() => setSelectedFigure(data.id)}>
        <div className="flex items-center justify-between w-full">
          {/* Left Section */}
          <div className="flex items-center gap-2 flex-1">
            <button onClick={() => setIsHidden({ type: 0, payload: { id: data.id } })}>
              {data.isHidden ? (
                <LuChevronRight size={23} />
              ) : (
                <LuChevronDown size={23} />
              )}
            </button>
            <button onClick={() => setFigureVisibility({ id: data.id, isVisible: !data.isVisible })}>
              <img
                src={data.isVisible ? Eye : EyeCrossed}
                className={classNames(data.isVisible ? "w-5 h-4" : "w-5 h-5")}
              />
            </button>
          </div>

          {/* Middle Section */}
          {data.value !== null && (
            <div className="flex-1 flex gap-2 justify-center font-bold">
              <h3>{isArea ? "Area" : "Distance"}</h3>
              <div>
                <span>{data.value.toFixed(2)}</span>
                <span>
                  {isArea ? (
                    <>
                      m<sup>2</sup>
                    </>
                  ) : (
                    "m"
                  )}
                </span>
              </div>
            </div>
          )}

          {/* Right Section */}
          <div className="flex-1 flex justify-end">
            <button onClick={() => setFigureToRemove(data.id)}>
              <LuX size={20} />
            </button>
          </div>
        </div>

        <AnimatePresence>
          {!data.isHidden && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{
                height: "auto",
                opacity: 1,
                transition: { duration: 0.3, ease: "easeOut" },
              }}
              exit={{
                height: 0,
                opacity: 0,
                transition: { duration: 0.2, ease: "easeIn" },
              }}
              className="w-full overflow-hidden"
            >
              <div className="flex flex-wrap gap-1 pt-2">
                {data.points.map(
                  (point: { x: number; y: number; z: number }, index: number) => (
                    <motion.div
                      key={index}
                      className="flex bg-dark/25 w-full rounded-lg py-1"
                    >
                      {[point.x, point.y, point.z].map((value, i) => (
                        <span
                          key={i}
                          className="flex-1 flex items-center justify-center"
                        >
                          {value.toFixed(2)}
                        </span>
                      ))}
                      <button>
                        <LuTrash2 size={15} className="flex-1 mr-4" onClick={() => setPointToRemove({ id: data.id, index: index })} />
                      </button>
                    </motion.div>
                  )
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  if (type === "volume") {
    return (
      <div className={classNames("flex flex-wrap items-center gap-2 bg-dark/35 w-full px-4 py-2 rounded-lg", selectedFigure === data.id ? "border-green border-2" : "cursor-pointer")} onClick={() => setSelectedFigure(data.id)}>
        <div className="flex items-center justify-between w-full">
          {/* Left Section */}
          <div className="flex items-center gap-2 flex-1">
            <button onClick={() => setIsHidden({ type: 1, payload: { id: data.id } })}>
              {data.isHidden ? (
                <LuChevronRight size={23} />
              ) : (
                <LuChevronDown size={23} />
              )}
            </button>
            <button onClick={() => setFigureVisibility({ id: data.id, isVisible: !data.isVisible })}>
              <img
                src={data.isVisible ? Eye : EyeCrossed}
                className={classNames(data.isVisible ? "w-5 h-4" : "w-5 h-5")}
              />
            </button>
            <button onClick={() => setVolumeToReload(data.id)}>
              <LuRefreshCw size={20} />
            </button>
          </div>

          {data.value !== null && (
            <div className="flex-1 flex gap-2 justify-center font-bold">
              <h3>Volume</h3>
              <div>
                <span>{data.value.toFixed(2)}</span>
                <span>
                  <>
                    m<sup>3</sup>
                  </>
                </span>
              </div>
            </div>
          )}

          <div className="flex-1 flex justify-end">
            <button onClick={() => setFigureToRemove(data.id)}>
              <LuX size={20} />
            </button>
          </div>
        </div>
        <AnimatePresence>
          {data.isHidden && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{
                height: "auto",
                opacity: 1,
                transition: { duration: 0.3, ease: "easeOut" },
              }}
              exit={{
                height: 0,
                opacity: 0,
                transition: { duration: 0.2, ease: "easeIn" },
              }}
              className="w-full overflow-hidden"
            >
              <div className="flex flex-wrap gap-1 pt-2">
                {data.points.map(
                  (point: { value: number, isVisible: boolean }, index: number) => (
                    <motion.div
                      key={index}
                      className="flex bg-dark/25 w-full rounded-lg py-1 pl-3"
                    >
                      <button onClick={() => setVolumeVisibility({ id: data.id, index: index, isVisible: !point.isVisible })}>
                        <img
                          src={point.isVisible ? Eye : EyeCrossed}
                          className={classNames(point.isVisible ? "w-5 h-4" : "w-5 h-5")}
                        />
                      </button>
                      <span
                        className="flex-1 flex items-center justify-center"
                      >
                        {point.value.toFixed(2)}
                      </span>
                      <button>
                        <LuTrash2 size={15} className="flex-1 mr-4" onClick={() => setPointToRemove({ id: data.id, index: index })} />
                      </button>
                    </motion.div>
                  )
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return null;
};

export default Measurement;
